import { Formik, Form } from 'formik';

import { Switch } from '@/core/components/form-components/SwitchField/Switch';
import { FormControl } from '@/core/components/form-components/FormControl';
import { Select } from '@/core/components/form-components/Input/Select';
import { Widget, WidgetBody, WidgetTitle } from '@/core/components/widget';
import { LoadingButton } from '@/core/components/Button/LoadingButton';
import { TextTip } from '@/core/components/Tip/TextTip';

import styles from './SettingsEdgeCompute.module.css';
import { validationSchema } from './SettingsEdgeCompute.validation';

export interface FormValues {
  EdgeAgentCheckinInterval: number;
  EnableEdgeComputeFeatures: boolean;
  DisableTrustOnFirstConnect: boolean;
  EnforceEdgeID: boolean;
}

interface Props {
  settings: FormValues;
  onSubmit(values: FormValues): void;
}

const checkinIntervalOptions = [
  {
    value: 5,
    label: '5 секунд',
  },
  {
    value: 10,
    label: '10 секунд',
  },
  {
    value: 30,
    label: '30 секунд',
  },
];

export function SettingsEdgeCompute({ settings, onSubmit }: Props) {
  const initialValues = {
    EdgeAgentCheckinInterval: settings ? settings.EdgeAgentCheckinInterval : 5,
    EnableEdgeComputeFeatures: settings
      ? settings.EnableEdgeComputeFeatures
      : false,
    DisableTrustOnFirstConnect: settings
      ? settings.DisableTrustOnFirstConnect
      : false,
    EnforceEdgeID: settings ? settings.EnforceEdgeID : false,
  };

  return (
    <div className="row">
      <Widget>
        <WidgetTitle icon="fa-laptop" title="Настройки Edge-вычислений" />
        <WidgetBody>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={() => validationSchema()}
            onSubmit={onSubmit}
            validateOnMount
          >
            {({
              values,
              errors,
              handleSubmit,
              setFieldValue,
              isSubmitting,
              isValid,
              dirty,
            }) => (
              <Form
                className="form-horizontal"
                onSubmit={handleSubmit}
                noValidate
              >
                <FormControl
                  inputId="edge_checkin"
                  label="Интервал опроса Edge-агентов по умолчанию"
                  size="medium"
                  tooltip="Интервал, с которым Edge-агенты по умолчанию связываются с сервером ShbDeployer. Влияет на скорость применения изменений в Edge-окружениях и на нагрузку на сервер. Для отдельного окружения интервал можно переопределить в его настройках."
                  errors={errors.EdgeAgentCheckinInterval}
                >
                  <Select
                    value={values.EdgeAgentCheckinInterval}
                    onChange={(e) =>
                      setFieldValue(
                        'EdgeAgentCheckinInterval',
                        parseInt(e.currentTarget.value, 10)
                      )
                    }
                    options={checkinIntervalOptions}
                  />
                </FormControl>

                <FormControl
                  inputId="edge_checkin"
                  label="Включить функции Edge-вычислений"
                  size="medium"
                  errors={errors.EnableEdgeComputeFeatures}
                >
                  <Switch
                    id="edge_enable"
                    name="edge_enable"
                    className="space-right"
                    checked={values.EnableEdgeComputeFeatures}
                    onChange={(e) =>
                      setFieldValue('EnableEdgeComputeFeatures', e)
                    }
                  />
                </FormControl>

                <TextTip color="blue">
                  Включает управление Edge-устройствами: Edge-группы, Edge-стеки
                  и Edge-задания.
                </TextTip>

                <FormControl
                  inputId="edge_enforce_id"
                  label="Требовать идентификатор окружения"
                  tooltip="Агент обязан передавать идентификатор Edge-окружения при подключении; подключения без идентификатора отклоняются."
                  size="medium"
                  errors={errors.EnforceEdgeID}
                >
                  <Switch
                    id="edge_enforce_id"
                    name="edge_enforce_id"
                    className="space-right"
                    checked={values.EnforceEdgeID}
                    onChange={(e) =>
                      setFieldValue('EnforceEdgeID', e.valueOf())
                    }
                  />
                </FormControl>

                <FormControl
                  inputId="edge_tofc"
                  label="Доверять при первом подключении"
                  tooltip="Новое Edge-устройство автоматически принимается при первом подключении. Если отключить, каждое устройство потребует ручного подтверждения администратором."
                  size="medium"
                  errors={errors.DisableTrustOnFirstConnect}
                >
                  <Switch
                    id="edge_disable_tofc"
                    name="edge_disable_tofc"
                    className="space-right"
                    checked={!values.DisableTrustOnFirstConnect}
                    onChange={(e) =>
                      setFieldValue('DisableTrustOnFirstConnect', !e.valueOf())
                    }
                  />
                </FormControl>

                <div className="form-group">
                  <div className="col-sm-12">
                    <LoadingButton
                      disabled={!isValid || !dirty}
                      dataCy="settings-edgeComputeButton"
                      className={styles.saveButton}
                      isLoading={isSubmitting}
                      loadingText="Сохранение…"
                    >
                      Сохранить настройки
                    </LoadingButton>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </WidgetBody>
      </Widget>
    </div>
  );
}
